import * as THREE from "three";
import gsap from "gsap";
import { episodes } from "../sources";
import vertexShader from "../shaders/episodeCard/vertex.glsl";
import fragmentShader from "../shaders/episodeCard/fragment.glsl";

const CORNER_RADIUS = 16; // px, matches the Figma card radius
const WARP_AMP = 0.095; // reference: WARP_MASK_AMP
const ACTIVE_MIX_DURATION = 0.25;

// Pure render layer for EpisodeSlider's card carousel — "HTML over WebGL":
// EpisodeSlider owns the real DOM (input, hit-testing, layout), this class
// owns a second scene/camera rendered into the SAME canvas via a direct
// extra renderer.render() call from Renderer.js, bypassing the main
// composer's grade/FXAA pipeline so the slider reads flat/clean. Mirrors
// the existing SequenceBG-owns-bgScene/bgCamera split.
//
// One plane + one <video>/<img> texture pair PER EPISODE, permanently.
// Geometry is a flat, unsubdivided PlaneGeometry(1,1) — confirmed against
// the real reference (hyundai-sa-fe's card shader) that the whole effect
// (warp, crossfade, rounded mask, scrim, dim) lives entirely in the
// fragment shader; nothing here displaces vertices.
export default class EpisodeSliderGL {
  constructor(experience) {
    this.experience = experience;

    this.visible = false;

    this.setInstance();
    this.buildPlanes();
  }

  setInstance() {
    this.scene = new THREE.Scene();

    const { width, height } = this.experience.sizes;
    // 1 CSS px = 1 world unit, origin at the CSS top-left, Y flipped (CSS
    // Y grows down, world Y grows up) — so a DOM rect's left/top/width/
    // height maps onto plane position/scale with no extra conversion math.
    this.camera = new THREE.OrthographicCamera(0, width, 0, -height, -1000, 1000);
    this.camera.position.z = 1;
    this.camera.lookAt(0, 0, 0);
    this.camera.updateProjectionMatrix();
  }

  buildPlanes() {
    const geometry = new THREE.PlaneGeometry(1, 1);
    const textureLoader = new THREE.TextureLoader();

    this.cards = episodes.map((episode) => {
      // Not appended to the DOM: a muted <video> decodes/plays fine
      // detached in evergreen browsers, and programmatic .play() on muted
      // media is allowed without a user gesture (same assumption
      // VideoTextDom's own video already relies on). Preloaded
      // immediately, not lazy on activation.
      const video = document.createElement("video");
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.loop = true;
      video.preload = "auto";
      video.src = episode.video;
      video.load();

      const videoTexture = new THREE.VideoTexture(video);
      videoTexture.colorSpace = THREE.SRGBColorSpace;

      const imageTexture = textureLoader.load(episode.image);
      imageTexture.colorSpace = THREE.SRGBColorSpace;

      const material = new THREE.ShaderMaterial({
        transparent: true,
        depthWrite: false,
        depthTest: false,
        uniforms: {
          uImageTex: { value: imageTexture },
          uVideoTex: { value: videoTexture },
          uMix: { value: 0 },
          uVelocity: { value: 0 },
          uWarpAmp: { value: WARP_AMP },
          uMeshSize: { value: new THREE.Vector2(1, 1) },
          uRadius: { value: CORNER_RADIUS },
          // Starts fully dimmed — no card is "active" until the very
          // first setActive() call in reveal().
          uDim: { value: 1 },
          uPadX: { value: 0 },
          uScale: { value: 1 },
          uParallax: { value: new THREE.Vector2(0, 0) },
          uTexRepeatA: { value: new THREE.Vector2(1, 1) },
          uTexOffsetA: { value: new THREE.Vector2(0, 0) },
          uTexRepeatB: { value: new THREE.Vector2(1, 1) },
          uTexOffsetB: { value: new THREE.Vector2(0, 0) },
        },
        vertexShader,
        fragmentShader,
      });

      const mesh = new THREE.Mesh(geometry, material);
      mesh.visible = false;
      this.scene.add(mesh);

      return { episode, video, mesh, material };
    });
  }

  // Called every tick from EpisodeSlider.update() with each card's DOM
  // element (index-aligned with `episodes`/`this.cards`) — reads its live
  // getBoundingClientRect() and mirrors it onto the matching plane. Cards
  // outside the visible viewport window just get rects outside the
  // camera's visible region; no special-casing needed.
  sync(cardElements) {
    if (!this.visible) return;

    for (let i = 0; i < this.cards.length; i++) {
      const rect = cardElements[i].getBoundingClientRect();
      const { mesh, material } = this.cards[i];

      mesh.position.set(rect.left + rect.width / 2, -(rect.top + rect.height / 2), 0);
      mesh.scale.set(rect.width, rect.height, 1);
      mesh.visible = rect.width > 0 && rect.height > 0;
      material.uniforms.uMeshSize.value.set(rect.width, rect.height);
    }
  }

  setVelocity(velocity) {
    for (const { material } of this.cards) {
      material.uniforms.uVelocity.value = velocity;
    }
  }

  // Fired once per activation change (from EpisodeSlider.goTo()), not
  // continuous per-tick math — a discrete "index changed" event.
  setActive(activeIndex) {
    this.cards.forEach(({ material, video }, index) => {
      const isActive = index === activeIndex;
      gsap.to(material.uniforms.uMix, {
        value: isActive ? 1 : 0,
        duration: ACTIVE_MIX_DURATION,
        ease: "power2.out",
      });
      gsap.to(material.uniforms.uDim, {
        value: isActive ? 0 : 1,
        duration: ACTIVE_MIX_DURATION,
        ease: "power2.out",
      });

      if (isActive) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  }

  // Flips the render-gate (see Renderer.js) and starts the initially-
  // active card's video — called from EpisodeSlider.reveal(), not show():
  // show() fires while the curtain is still fully opaque and holds for a
  // while before anything is visible, so there's no reason to spend
  // decode/playback cycles before reveal() actually shows the slider.
  setVisible(visible, activeIndex) {
    this.visible = visible;
    if (visible) this.setActive(activeIndex);
  }

  resize(width, height) {
    this.camera.left = 0;
    this.camera.right = width;
    this.camera.top = 0;
    this.camera.bottom = -height;
    this.camera.updateProjectionMatrix();
  }
}
